import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { UserCircle, X, Loader2, CheckCircle, AlertCircle, Play, Download, Upload, Link2, Music, Image, Eye } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const FabricAvatarNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [imageLocal, setImageLocal] = useState(null);
  const [audioLocal, setAudioLocal] = useState(null);
  const [resolution, setResolution] = useState(data.resolution || '720p');
  const [localError, setLocalError] = useState(null);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openVideoPreview } = useMediaPreview();

  // Check for connected inputs
  const imageEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputImage');
  const audioEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputAudio');
  
  // Get connected node data
  const getConnectedNodeData = (edge) => {
    if (!edge) return null;
    const sourceNode = nodes.find((n) => n.id === edge.source);
    return sourceNode?.data;
  };
  
  const imageInputData = getConnectedNodeData(imageEdge);
  const audioInputData = getConnectedNodeData(audioEdge);
  
  // Determine which files to use (connected or local upload)
  const imageSource = imageInputData?.hasOutput ? { 
    file: imageInputData.file, 
    preview: imageInputData.outputUrl || imageInputData.preview,
    name: imageInputData.fileName || 'connected_image',
    fromConnection: true
  } : imageLocal;
  
  const audioSource = audioInputData?.hasOutput ? { 
    file: audioInputData.file, 
    preview: audioInputData.outputUrl || audioInputData.preview,
    name: audioInputData.fileName || 'connected_audio',
    fromConnection: true
  } : audioLocal;

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageLocal({ file, preview: e.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleAudioChange = (file) => {
    if (!file) return;
    setAudioLocal({ file, preview: URL.createObjectURL(file), name: file.name });
  };

  const handleSourcePickImage = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) handleImageChange(result.file);
      else if (result.url) setImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);

  const handleSourcePickAudio = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) handleAudioChange(result.file);
      else if (result.url) setAudioLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
    });
    openSourcePicker(id, ['audio'], 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/mpeg');
  }, [id, openSourcePicker]);

  // Fetch file from URL if coming from connection
  const fetchFileFromUrl = async (url, filename, isAudio = false) =>
    blobToFile(url, filename, isAudio ? 'audio/mp3' : 'image/jpeg');

  const handleProcess = useCallback(async () => {
    let imageFile = imageSource?.file;
    let audioFile = audioSource?.file;
    const directParams = { resolution };
    const files = [];

    if (!imageFile && imageSource?.preview) {
      if (imageSource.preview.startsWith('http')) {
        directParams.image_url = imageSource.preview;
      } else {
        imageFile = await fetchFileFromUrl(imageSource.preview, imageSource.name, false);
      }
    }
    if (imageFile) files.push({ key: 'image_url', file: imageFile });

    if (!audioFile && audioSource?.preview) {
      if (audioSource.preview.startsWith('http')) {
        directParams.audio_url = audioSource.preview;
      } else {
        audioFile = await fetchFileFromUrl(audioSource.preview, audioSource.name, true);
      }
    }
    if (audioFile) files.push({ key: 'audio_url', file: audioFile });

    if (!imageFile && !directParams.image_url) {
      setLocalError('No se pudo obtener la imagen. Verifica la conexión o sube una imagen directamente.');
      return;
    }
    if (!audioFile && !directParams.audio_url) {
      setLocalError('No se pudo obtener el audio. Verifica la conexión o sube un audio directamente.');
      return;
    }

    setLocalError(null);
    resetExec();

    const result = await processNode({
      resultType: 'fabric_avatar',
      files,
      params: directParams,
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'fabricAvatar',
        filename: `avatar_${imageSource?.name || 'image'}_${audioSource?.name || 'audio'}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, imageSource, audioSource, resolution, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const hasValidImage = imageSource?.preview || imageSource?.file || (imageInputData?.hasOutput);
  const hasValidAudio = audioSource?.preview || audioSource?.file || (audioInputData?.hasOutput);
  const canProcess = hasValidImage && hasValidAudio;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', hasValidImage, { connected: !!imageEdge }), inputNeed('audio', hasValidAudio, { connected: !!audioEdge })]);

  return (
    <div className={`node-container fabric-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="fabric-avatar-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="fabricavatar-reconfigure-notice"
        />
      )}
      <Handle
        type="target"
        position={Position.Left}
        id="inputImage"
        className="node-handle input-handle fabric-handle-image"
        style={{ top: '30%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="inputAudio"
        className="node-handle input-handle fabric-handle-audio"
        style={{ top: '55%' }}
      />
      
      {/* Header */}
      <div className="node-header fabric-header">
        <div className="node-header-left">
          <UserCircle size={16} />
          <span>Avatar Parlante</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content fabric-content">
        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Creando avatar...</span>
            </div>
            <p className="node-processing-hint">
              Generando avatar ({resolution})
            </p>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <video 
              src={data.outputUrl} 
              controls 
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Avatar Parlante')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="fabricavatar-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="fabricavatar-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openVideoPreview(data.outputUrl, 'Avatar Parlante')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `avatar_${Date.now()}.mp4`); }}
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="fabricavatar" testIdPrefix="fabricavatar-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Avatar creado</span>
            </div>
          </div>
        ) : (
          <>
            {/* Input Files Grid */}
            <div className="fabric-inputs-grid">
              {/* Image Input */}
              <div className="fabric-input-slot">
                <span className="fabric-input-label">
                  <Image size={10} />
                  Imagen
                  {imageEdge && <Link2 size={10} className="fabric-connected-icon" />}
                </span>
                {imageSource?.preview ? (
                  <div className="fabric-preview-box image">
                    <img src={imageSource.preview} alt="Source" />
                    {!imageSource.fromConnection && (
                      <button 
                        className="fabric-clear-btn"
                        onClick={() => setImageLocal(null)}
                      >
                        <X size={10} />
                      </button>
                    )}
                    {imageSource.fromConnection && (
                      <div className="fabric-connected-badge">
                        <Link2 size={8} />
                      </div>
                    )}
                  </div>
                ) : imageEdge ? (
                  <div className="fabric-waiting-input">
                    <Link2 size={14} />
                    <span>Esperando...</span>
                  </div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickImage} data-testid="load-btn-fabric-image">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>

              {/* Audio Input */}
              <div className="fabric-input-slot">
                <span className="fabric-input-label">
                  <Music size={10} />
                  Audio
                  {audioEdge && <Link2 size={10} className="fabric-connected-icon" />}
                </span>
                {audioSource?.name ? (
                  <div className="fabric-preview-box audio">
                    <Music size={20} />
                    <span className="fabric-audio-name">{audioSource.name.slice(0, 12)}...</span>
                    {!audioSource.fromConnection && (
                      <button 
                        className="fabric-clear-btn"
                        onClick={() => setAudioLocal(null)}
                      >
                        <X size={10} />
                      </button>
                    )}
                    {audioSource.fromConnection && (
                      <div className="fabric-connected-badge">
                        <Link2 size={8} />
                      </div>
                    )}
                  </div>
                ) : audioEdge ? (
                  <div className="fabric-waiting-input">
                    <Link2 size={14} />
                    <span>Esperando...</span>
                  </div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickAudio} data-testid="load-btn-fabric-audio">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>
            </div>

            {/* Resolution Setting */}
            <div className="fabric-settings">
              <div className="fabric-setting">
                <label>
                  <Eye size={12} />
                  Resolución
                </label>
                <div className="fabric-options">
                  {['480p', '720p'].map((r) => (
                    <button
                      key={r}
                      className={`fabric-option ${resolution === r ? 'active' : ''}`}
                      onClick={() => setResolution(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Info text */}
            <p className="fabric-info">
              Crea videos de avatares parlantes hasta 60s
            </p>

            {/* Process Button */}
            <button
              className="node-process-btn fabric-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="fabric-generate-btn"
            >
              <Play size={14} />
              Generar Avatar
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        {/* Error Display */}
        <NodeError error={execError || localError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

FabricAvatarNode.displayName = 'FabricAvatarNode';

export default FabricAvatarNode;
